import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  IconButton,
  Pagination,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { freshAccessToken } from '../auth/accessToken';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BugReportIcon from '@mui/icons-material/BugReport';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { getCampaignDetails, getJobReport, resendEmail, bulkResendEmails, updateCampaignStatus, deleteCampaign, resendToSelected, checkCampaignDuplicates, fixCampaignCounts, retriggerPush, cancelCampaign, CampaignDetails } from '../services/ecm-api';
import axios from '../api/axios';

export default function JobDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [bulkResending, setBulkResending] = useState(false);
  const [retriggeringPush, setRetriggeringPush] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recipientsPerPage = 100;
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [resendingSelected, setResendingSelected] = useState(false);
  const [removingRecipient, setRemovingRecipient] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Add Recipients state
  const [addRecipientsOpen, setAddRecipientsOpen] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [recipientFile, setRecipientFile] = useState<File | null>(null);
  const [sendingEmails, setSendingEmails] = useState(false);

  // Check Duplicates / Diagnostics state
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState<any>(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [cancellingCampaign, setCancellingCampaign] = useState(false);

  const loadJob = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await getCampaignDetails(id);
      console.log('📊 Campaign Details Loaded:', data);
      console.log('📊 First recipient fields:', data.recipients?.[0] ? Object.keys(data.recipients[0]) : 'No recipients');
      console.log('📊 First recipient data:', data.recipients?.[0]);
      setCampaign(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJob();
    const interval = setInterval(loadJob, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, [id]);

  const handleDownloadReport = async () => {
    if (!id) return;

    try {
      const blob = await getJobReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-report-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download report:', err);
    }
  };

  const handleResendEmail = async (recipientEmail: string) => {
    if (!id || !window.confirm(`Resend email to ${recipientEmail}?`)) return;

    try {
      setResendingEmail(recipientEmail);
      await resendEmail(id, recipientEmail);
      
      // Reload campaign details to get updated send count
      await loadJob();
      
      alert(`Email successfully resent to ${recipientEmail}`);
    } catch (err: any) {
      console.error('Failed to resend email:', err);
      alert(err.response?.data?.message || 'Failed to resend email');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleBulkResend = async () => {
    if (!id || !campaign) return;
    
    const unsentCount = campaign.recipients.filter(r => !r.email_sent).length;
    if (unsentCount === 0) {
      alert('All recipients have already received emails.');
      return;
    }
    
    if (!window.confirm(`Resend emails to all ${unsentCount} recipients who haven't received an email?`)) return;

    try {
      setBulkResending(true);
      const result = await bulkResendEmails(id);
      
      // Reload campaign details
      await loadJob();
      
      alert(`✅ Bulk resend complete!\n\nSent: ${result.sent_count}\nFailed: ${result.failed_count}`);
    } catch (err: any) {
      console.error('Failed to bulk resend:', err);
      alert(err.response?.data?.message || 'Failed to bulk resend emails');
    } finally {
      setBulkResending(false);
    }
  };

  const handleSelectRecipient = (email: string) => {
    setSelectedRecipients(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleSelectAllPage = () => {
    if (!campaign) return;
    
    const currentPageRecipients = campaign.recipients
      .slice((currentPage - 1) * recipientsPerPage, currentPage * recipientsPerPage)
      .map(r => r.email || r.student_id)
      .filter((email): email is string => email !== undefined && email !== 'N/A');
    
    const allSelected = currentPageRecipients.every(email => selectedRecipients.includes(email));
    
    if (allSelected) {
      // Deselect all on current page
      setSelectedRecipients(prev => prev.filter(email => !currentPageRecipients.includes(email)));
    } else {
      // Select all on current page
      setSelectedRecipients(prev => [...new Set([...prev, ...currentPageRecipients])]);
    }
  };

  const handleResendSelected = async () => {
    if (selectedRecipients.length === 0 || !id) return;
    
    if (!window.confirm(`Resend emails to ${selectedRecipients.length} selected recipients?`)) return;
    
    try {
      setResendingSelected(true);
      const result = await resendToSelected(id, selectedRecipients);
      
      // Reload campaign details
      await loadJob();
      
      alert(`✅ Resend to selected complete!\n\nSent: ${result.sent_count}\nFailed: ${result.failed_count}`);
      setSelectedRecipients([]);  // Clear selections
    } catch (err: any) {
      console.error('Failed to resend to selected:', err);
      alert(err.response?.data?.message || 'Failed to resend to selected recipients');
    } finally {
      setResendingSelected(false);
    }
  };

  const handleRetriggerPush = async () => {
    if (!id || !campaign) return;
    const displayName = campaign.modo_title || (campaign as any).name || campaign.title;
    if (!window.confirm(`Re-send the Modo push notification "${displayName}" to all My Laurus app users?\n\nThis will send a NEW push notification immediately.`)) return;
    try {
      setRetriggeringPush(true);
      const result = await retriggerPush(id);
      await loadJob();
      alert(`✅ Push notification sent!\n\nModo message ID: ${result.modo_message_id}\nSent at: ${new Date(result.triggered_at).toLocaleString()}`);
    } catch (err: any) {
      console.error('Failed to retrigger push:', err);
      alert(err.response?.data?.message || 'Failed to send push notification. Check if session cookie needs refresh.');
    } finally {
      setRetriggeringPush(false);
    }
  };

  const handleRemoveRecipient = async (recipientEmail: string) => {
    if (!id || !window.confirm(`Remove ${recipientEmail} from this campaign?\n\nThis will permanently delete this recipient from the campaign.`)) return;

    try {
      setRemovingRecipient(recipientEmail);
      
      // Make API call to remove recipient (with a fresh token, as every request: auth/accessToken.ts)
      const token = await freshAccessToken();
      await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/campaigns/${id}/recipients/${encodeURIComponent(recipientEmail)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-dev-user-id': JSON.parse(sessionStorage.getItem('user') || '{}').email,
          'Content-Type': 'application/json'
        }
      });
      
      // Reload campaign details
      await loadJob();
      
      alert(`✅ Recipient ${recipientEmail} removed successfully`);
    } catch (err: any) {
      console.error('Failed to remove recipient:', err);
      alert('Failed to remove recipient. Please try again.');
    } finally {
      setRemovingRecipient(null);
    }
  };

  const handleAddSingleRecipient = async () => {
    if (!newRecipientEmail.trim() || !id) return;
    setSendingEmails(true);
    try {
      await axios.post(`/campaigns/${id}/add-recipient`, {
        email: newRecipientEmail.trim(),
        name: newRecipientName.trim() || newRecipientEmail.trim(),
      });
      setNewRecipientEmail('');
      setNewRecipientName('');
      await loadJob();
      alert(`✅ Email sent to ${newRecipientEmail}`);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add recipient');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleAddRecipientsFromCSV = async () => {
    if (!recipientFile || !id) return;
    setSendingEmails(true);
    try {
      const formData = new FormData();
      formData.append('file', recipientFile);
      await axios.post(`/campaigns/${id}/add-recipients-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecipientFile(null);
      setAddRecipientsOpen(false);
      await loadJob();
      alert('✅ Recipients added from CSV successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to upload CSV');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleCheckDuplicates = async () => {
    if (!id) return;
    setDiagnosticOpen(true);
    setDiagnosticData(null);
    setDiagnosticLoading(true);
    try {
      const result = await checkCampaignDuplicates(id);
      setDiagnosticData(result);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to check duplicates');
      setDiagnosticOpen(false);
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleFixCounts = async () => {
    if (!id) return;
    setDiagnosticLoading(true);
    try {
      const result = await fixCampaignCounts(id);
      alert(`Counts fixed!\nOld: ${result.old_counts.emails_sent}/${result.old_counts.total_recipients}\nNew: ${result.new_counts.emails_sent}/${result.new_counts.total_recipients}`);
      setDiagnosticOpen(false);
      await loadJob();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to fix counts');
    } finally {
      setDiagnosticLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'archived' | 'deleted') => {
    if (!id || !campaign) return;
    
    // If changing to deleted and already deleted, use DELETE endpoint to permanently remove
    const isAlreadyDeleted = campaign.status === 'deleted';
    const isPermanentDelete = newStatus === 'deleted' && isAlreadyDeleted;
    
    const confirmMessages: Record<string, string> = {
      active: 'Restore this campaign to Active status?',
      archived: 'Archive this campaign? It will be automatically deleted after 90 days.',
      deleted: isPermanentDelete 
        ? 'PERMANENTLY DELETE this campaign from the database? This CANNOT be undone!'
        : 'Move this campaign to Deleted status?'
    };
    
    if (!window.confirm(confirmMessages[newStatus])) return;

    try {
      setChangingStatus(true);
      
      if (isPermanentDelete) {
        // Permanently delete from database
        await deleteCampaign(id);
      } else {
        // Change status
        await updateCampaignStatus(id, newStatus);
      }
      
      // Reload to show updated status
      await loadJob();
      
      const successMessages: Record<string, string> = {
        active: 'Campaign restored to Active',
        archived: 'Campaign archived successfully',
        deleted: isPermanentDelete 
          ? 'Campaign permanently deleted from database'
          : 'Campaign moved to Deleted status'
      };
      
      alert(successMessages[newStatus]);
      
      // Navigate away after any delete operation
      if (newStatus === 'deleted') {
        navigate('/ECM/Dashboard');
      }
    } catch (err: any) {
      console.error(`Failed to change status to ${newStatus}:`, err);
      alert(err.response?.data?.message || `Failed to change campaign status`);
    } finally {
      setChangingStatus(false);
    }
  };

  const handleCancelCampaign = async () => {
    if (!id || !campaign) return;
    if (!window.confirm(
      `⛔ STOP SEND — "${campaign.title}"\n\nThis will cancel the campaign immediately. Any emails already sent cannot be recalled.\nAn in-progress send will stop within the next 10 emails.\n\nContinue?`
    )) return;
    try {
      setCancellingCampaign(true);
      const result = await cancelCampaign(id);
      await loadJob();
      alert(`Campaign stopped.\n${result.emails_sent_so_far} of ${result.total_recipients} emails had been sent.`);
    } catch (err: any) {
      console.error('Failed to cancel campaign:', err);
      alert(err.response?.data?.message || 'Failed to cancel campaign');
    } finally {
      setCancellingCampaign(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'error';
      case 'sending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading && !campaign) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!campaign) {
    return <Alert severity="warning">Campaign not found</Alert>;
  }

  const progress = campaign.recipients.length > 0 
    ? (campaign.recipients.filter(r => r.email_sent).length / campaign.recipients.length) * 100 
    : 0;

  return (
    <Box sx={{
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      minHeight: '100vh',
      p: 3,
      // Override MUI component styles
      '& .MuiPaper-root': {
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiTableCell-root': {
        color: 'var(--text-primary)',
        borderColor: 'var(--card-border)'
      },
      '& .MuiTableCell-head': {
        backgroundColor: 'var(--bg-secondary)',
        fontWeight: 'bold'
      },
      '& .MuiTypography-root': {
        color: 'inherit'
      }
    }}>
      {/* Sticky header and summary sections */}
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 10, 
        bgcolor: 'var(--bg-primary)',
        pb: 2,
        mb: 1
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/ECM/Dashboard')}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Campaign Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadJob}
            sx={{ minWidth: 140, height: 56 }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setAddRecipientsOpen(true)}
            sx={{ minWidth: 140, height: 56, borderColor: 'var(--accent)', color: 'var(--accent)', '&:hover': { borderColor: 'var(--accent-hover)', backgroundColor: 'var(--bg-secondary)' } }}
          >
            Add Recipients
          </Button>
          <Button
            variant="outlined"
            startIcon={<BugReportIcon />}
            onClick={handleCheckDuplicates}
            sx={{ minWidth: 140, height: 56, borderColor: 'var(--accent)', color: 'var(--accent)', '&:hover': { borderColor: 'var(--accent-hover)', backgroundColor: 'var(--bg-secondary)' } }}
          >
            Check Duplicates
          </Button>
          
          {/* Resend to Selected Button */}
          {selectedRecipients.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleResendSelected}
              disabled={resendingSelected}
              sx={{ minWidth: 140, height: 56 }}
            >
              {resendingSelected ? 'Sending...' : `Resend to Selected (${selectedRecipients.length})`}
            </Button>
          )}
          
          {/* Bulk Resend Button - show if there are unsent emails */}
          {campaign.recipients.filter(r => !r.email_sent).length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleBulkResend}
              disabled={bulkResending}
              sx={{ minWidth: 140, height: 56 }}
            >
              {bulkResending ? 'Sending...' : `Resend All (${campaign.recipients.filter(r => !r.email_sent).length})`}
            </Button>
          )}

          {/* Modo Push Re-send Button — only for modo_push campaigns */}
          {(campaign.campaign_type === 'modo_push' || campaign.communication_channels?.includes('modo_push')) && (
            <Tooltip title="Re-send this push notification to all My Laurus app users">
              <span>
                <Button
                  variant="contained"
                  startIcon={<NotificationsActiveIcon />}
                  onClick={handleRetriggerPush}
                  disabled={retriggeringPush}
                  sx={{
                    minWidth: 140,
                    height: 56,
                    backgroundColor: '#003366',
                    '&:hover': { backgroundColor: '#002244' },
                    color: '#fff',
                  }}
                >
                  {retriggeringPush ? 'Sending Push...' : '📱 Re-send Push'}
                </Button>
              </span>
            </Tooltip>
          )}
          
          {campaign.status === 'completed' && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReport}
              sx={{ minWidth: 140, height: 56 }}
            >
              Download Report
            </Button>
          )}
          
          {/* Stop Send button — shown when campaign is actively sending or stuck in sending state */}
          {(campaign.status === 'sending' || campaign.status === 'active') && (
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelCampaign}
              disabled={cancellingCampaign}
              sx={{ minWidth: 140, height: 56 }}
            >
              {cancellingCampaign ? 'Stopping...' : '⛔ Stop Send'}
            </Button>
          )}

          {/* Status Change Buttons */}
          {campaign.status === 'active' && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleStatusChange('archived')}
              disabled={changingStatus}
              sx={{ minWidth: 140, height: 56 }}
            >
              {changingStatus ? 'Processing...' : 'Archive'}
            </Button>
          )}
          
          {campaign.status === 'completed' && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => handleStatusChange('archived')}
              disabled={changingStatus}
              sx={{ minWidth: 140, height: 56 }}
            >
              {changingStatus ? 'Processing...' : 'Archive'}
            </Button>
          )}
          
          {campaign.status === 'archived' && (
            <>
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleStatusChange('active')}
                disabled={changingStatus}
                sx={{ minWidth: 140, height: 56 }}
              >
                {changingStatus ? 'Processing...' : 'Restore to Active'}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleStatusChange('deleted')}
                disabled={changingStatus}
                sx={{ minWidth: 140, height: 56 }}
              >
                {changingStatus ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </>
          )}
          
          {campaign.status === 'cancelled' && (
            <Button
              variant="outlined"
              color="success"
              onClick={() => handleStatusChange('active')}
              disabled={changingStatus}
              sx={{ minWidth: 140, height: 56 }}
            >
              {changingStatus ? 'Processing...' : 'Restore to Active'}
            </Button>
          )}

          {campaign.status === 'deleted' && (
            <>
              <Button
                variant="outlined"
                color="success"
                onClick={() => handleStatusChange('archived')}
                disabled={changingStatus}
                sx={{ minWidth: 140, height: 56 }}
              >
                {changingStatus ? 'Processing...' : 'Move to Archived'}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleStatusChange('deleted')}
                disabled={changingStatus}
                sx={{ minWidth: 140, height: 56 }}
              >
                {changingStatus ? 'Deleting...' : 'Delete from Database'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Stack spacing={2}>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Campaign Name
              </Typography>
              <Typography variant="h6">{campaign.title}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={campaign.status}
                color={getStatusColor(campaign.status)}
                sx={{
                  mt: 0.5,
                  ...(campaign.status === 'failed' && campaign.last_error && {
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  })
                }}
                onClick={campaign.status === 'failed' && campaign.last_error ? () => setShowErrorDialog(true) : undefined}
                title={campaign.status === 'failed' && campaign.last_error ? 'Click to see error details' : undefined}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Target Audience
              </Typography>
              <Typography variant="body1">{campaign.target_audience}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {campaign.created_at ? new Date(campaign.created_at + (campaign.created_at.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }) : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Sent At
              </Typography>
              <Typography variant="body1">
                {campaign.sent_at ? new Date(campaign.sent_at + (campaign.sent_at.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                }) : 'Not Sent'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Progress
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {campaign.recipients.filter(r => r.email_sent).length} / {campaign.recipients.length} emails sent
              </Typography>
              <Typography variant="body2">{progress.toFixed(1)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
                <Typography variant="subtitle2" color="primary.dark">
                  Total Recipients
                </Typography>
                <Typography variant="h4" color="primary.dark">{campaign.recipients.length}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="subtitle2" color="success.dark">
                  Emails Sent
                </Typography>
                <Typography variant="h4" color="success.dark">
                  {campaign.recipients.filter(r => r.email_sent).length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                <Typography variant="subtitle2" color="info.dark">
                  Responses
                </Typography>
                <Typography variant="h4" color="info.dark">
                  {campaign.recipients.filter(r => r.survey_completed).length}
                </Typography>
              </Paper>
            </Grid>

          </Grid>
        </Paper>
      </Stack>
      </Box>
      {/* End sticky section */}

      {/* Scrollable Recipients Section */}
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recipients ({campaign.recipients.length})
            {selectedRecipients.length > 0 && (
              <Chip 
                label={`${selectedRecipients.length} selected`} 
                color="secondary" 
                size="small" 
                sx={{ ml: 2 }}
                onDelete={() => setSelectedRecipients([])}
              />
            )}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedRecipients.length > 0 &&
                        campaign.recipients
                          .slice((currentPage - 1) * recipientsPerPage, currentPage * recipientsPerPage)
                          .filter(r => (r.email || r.student_id) && (r.email || r.student_id) !== 'N/A')
                          .some(r => {
                            const email = r.email || r.student_id;
                            return email && !selectedRecipients.includes(email);
                          })
                      }
                      checked={
                        campaign.recipients
                          .slice((currentPage - 1) * recipientsPerPage, currentPage * recipientsPerPage)
                          .filter(r => (r.email || r.student_id) && (r.email || r.student_id) !== 'N/A')
                          .length > 0 &&
                        campaign.recipients
                          .slice((currentPage - 1) * recipientsPerPage, currentPage * recipientsPerPage)
                          .filter(r => (r.email || r.student_id) && (r.email || r.student_id) !== 'N/A')
                          .every(r => {
                            const email = r.email || r.student_id;
                            return email ? selectedRecipients.includes(email) : false;
                          })
                      }
                      onChange={handleSelectAllPage}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Times Resent</TableCell>
                  <TableCell>Email Sent</TableCell>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Survey Completed</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaign.recipients
                  .slice((currentPage - 1) * recipientsPerPage, currentPage * recipientsPerPage)
                  .map((recipient, index) => {
                  // Log first recipient's actual data for debugging
                  if (index === 0) {
                    console.log('🔍 First recipient data:', recipient);
                  }
                  
                  const recipientAny = recipient as any;
                  
                  // Email address - backend stores in 'email' field (camelCase)
                  const emailAddress = recipient.email || 
                    recipientAny.student_id || 
                    recipient.student_email || 
                    recipientAny.emailAddress || 
                    'N/A';
                  
                  // Check firstName/lastName (camelCase) first - that's what backend uses
                  const firstName = recipientAny.firstName || recipient.first_name || '';
                  const lastName = recipientAny.lastName || recipient.last_name || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  
                  const studentName = fullName || 
                    recipient.student_name || 
                    recipientAny.name || 
                    'N/A';
                  
                  return (
                    <TableRow key={recipientAny.student_id || index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRecipients.includes(emailAddress)}
                          onChange={() => handleSelectRecipient(emailAddress)}
                          disabled={emailAddress === 'N/A'}
                        />
                      </TableCell>
                      <TableCell>{studentName}</TableCell>
                      <TableCell>{emailAddress}</TableCell>
                      <TableCell>{recipientAny.email_send_count !== undefined ? recipientAny.email_send_count : 0}</TableCell>
                      <TableCell>
                        <Chip 
                          label={recipient.email_sent ? 'Yes' : 'No'} 
                          color={recipient.email_sent ? 'success' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {recipient.email_sent ? (
                          recipientAny.email_sent_at 
                            ? new Date(recipientAny.email_sent_at + (recipientAny.email_sent_at.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })
                            : 'N/A'
                        ) : (
                          recipientAny.email_send_count > 0 && recipientAny.email_sent_at
                            ? new Date(recipientAny.email_sent_at + (recipientAny.email_sent_at.endsWith('Z') ? '' : 'Z')).toLocaleString('en-US', {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })
                            : 'Not Sent'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={recipient.survey_completed ? 'Yes' : 'No'} 
                          color={recipient.survey_completed ? 'success' : 'default'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleResendEmail(emailAddress)}
                          disabled={resendingEmail === emailAddress || emailAddress === 'N/A'}
                          color="primary"
                          title={emailAddress === 'N/A' ? 'No email available' : 'Resend email'}
                        >
                          {resendingEmail === emailAddress ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SendIcon fontSize="small" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRecipient(emailAddress)}
                          disabled={removingRecipient === emailAddress || emailAddress === 'N/A'}
                          color="error"
                          title={emailAddress === 'N/A' ? 'No email available' : 'Remove recipient'}
                        >
                          {removingRecipient === emailAddress ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PersonRemoveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {campaign.recipients.length > recipientsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((currentPage - 1) * recipientsPerPage) + 1}-{Math.min(currentPage * recipientsPerPage, campaign.recipients.length)} of {campaign.recipients.length} recipients
              </Typography>
              <Pagination
                count={Math.ceil(campaign.recipients.length / recipientsPerPage)}
                page={currentPage}
                onChange={(_event, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      </Stack>

      {/* ── Add Recipients Dialog ─────────────────────────────────────── */}
      <Dialog
        open={addRecipientsOpen}
        onClose={() => { setAddRecipientsOpen(false); setNewRecipientEmail(''); setNewRecipientName(''); setRecipientFile(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Recipients — {campaign.title}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Option 1: Add Single Email</Typography>
          <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleAddSingleRecipient(); }}>
            <TextField
              placeholder="Recipient Name"
              value={newRecipientName}
              onChange={e => setNewRecipientName(e.target.value)}
              fullWidth size="small" sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="email"
                placeholder="recipient@example.com"
                value={newRecipientEmail}
                onChange={e => setNewRecipientEmail(e.target.value)}
                required fullWidth size="small"
              />
              <Button type="submit" variant="contained" disabled={sendingEmails || !newRecipientEmail.trim()}>
                {sendingEmails ? '…' : 'Add'}
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Option 2: Upload CSV File</Typography>
          <Paper
            sx={{ p: 3, textAlign: 'center', border: '2px dashed', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            onClick={() => document.getElementById('jd-recipient-csv')?.click()}
          >
            <input
              type="file" accept=".csv" id="jd-recipient-csv" style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) setRecipientFile(e.target.files[0]); }}
            />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {recipientFile ? recipientFile.name : 'Click to select CSV file'}
            </Typography>
            <Typography variant="caption" color="text.secondary">CSV must have an "email" column</Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddRecipientsOpen(false); setRecipientFile(null); }} disabled={sendingEmails}>Cancel</Button>
          <Button onClick={handleAddRecipientsFromCSV} variant="contained" disabled={!recipientFile || sendingEmails}>
            {sendingEmails ? 'Uploading…' : 'Upload & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Failed Campaign Error Dialog ──────────────────────────────── */}
      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          ⚠️ Campaign Failed
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Error Details
          </Typography>
          <Box
            sx={{
              backgroundColor: 'rgba(211,47,47,0.08)',
              border: '1px solid',
              borderColor: 'error.light',
              borderRadius: 1,
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'error.dark',
            }}
          >
            {campaign?.last_error || 'No error details available.'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)} variant="contained" color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Check Duplicates / Diagnostic Dialog ─────────────────────── */}
      <Dialog
        open={diagnosticOpen}
        onClose={() => !diagnosticLoading && setDiagnosticOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Campaign Diagnostic — {campaign.title}</DialogTitle>
        <DialogContent>
          {diagnosticLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : diagnosticData ? (
            <Box>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Stored Counts</Typography>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography>Total Recipients: {diagnosticData.stored_counts.total_recipients}</Typography>
                <Typography>Emails Sent: {diagnosticData.stored_counts.emails_sent}</Typography>
                <Typography>Emails Failed: {diagnosticData.stored_counts.emails_failed}</Typography>
              </Paper>

              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Actual Counts (from recipients array)</Typography>
              <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography>Total Recipient Records: {diagnosticData.actual_counts.total_recipient_records}</Typography>
                <Typography>Sent Records: {diagnosticData.actual_counts.sent_records}</Typography>
                <Typography>Unsent Records: {diagnosticData.actual_counts.unsent_records}</Typography>
                <Typography fontWeight="bold" sx={{ mt: 1 }}>Unique Emails Total: {diagnosticData.actual_counts.unique_emails_total}</Typography>
                <Typography fontWeight="bold">Unique Sent: {diagnosticData.actual_counts.unique_sent_emails}</Typography>
                <Typography fontWeight="bold">Unique Unsent: {diagnosticData.actual_counts.unique_unsent_emails}</Typography>
              </Paper>

              {diagnosticData.duplicates?.has_duplicates && (
                <>
                  <Typography variant="h6" color="warning.main" sx={{ mt: 2, mb: 1 }}>
                    Duplicates Found ({diagnosticData.duplicates.duplicate_count})
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'warning.light', maxHeight: 200, overflow: 'auto' }}>
                    {Object.entries(diagnosticData.duplicates.duplicate_details).map(([email, details]: [string, any]) => (
                      <Box key={email} sx={{ mb: 1 }}>
                        <Typography fontWeight="bold" color="warning.dark">{email} (×{details.count})</Typography>
                        <Typography variant="body2" color="warning.dark">Sent: {details.sent_count}, Unsent: {details.unsent_count}</Typography>
                      </Box>
                    ))}
                  </Paper>
                </>
              )}

              {diagnosticData.unsent_sample?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Unsent Recipients (first {diagnosticData.unsent_sample.length})</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover', maxHeight: 200, overflow: 'auto' }}>
                    {diagnosticData.unsent_sample.map((r: any, i: number) => (
                      <Typography key={i} variant="body2">
                        {r.email} — {r.firstName} {r.lastName}{r.lastError ? ` (Error: ${r.lastError})` : ''}
                      </Typography>
                    ))}
                  </Paper>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagnosticOpen(false)} disabled={diagnosticLoading}>Close</Button>
          {diagnosticData && (
            <Button onClick={handleFixCounts} variant="contained" disabled={diagnosticLoading}>
              {diagnosticLoading ? 'Fixing…' : 'Fix Counts'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
